import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '@/hooks/useOnboarding';
import { Check, Sparkles, Trophy, ChevronRight } from 'lucide-react';
import { apiClient } from '@/api/apiClient';

export const ONBOARDING_TASKS = [
    { id: 'first_swipe', label: 'Faites votre premier swipe', navigateTo: '/swipe' },
    { id: 'view_brand_of_day', label: 'Voir la marque du jour', navigateTo: 'brand_of_day' },
    { id: 'like_item', label: 'Liker un vêtement', navigateTo: '/swipe' },
    { id: 'create_collection', label: 'Créez votre première collection', navigateTo: '/swipe', hint: 'collection' },
    { id: 'visit_brand_page', label: 'Visitez une page de marque', navigateTo: '/search', hint: 'brand_page' },
    { id: 'subscribe_brand', label: 'Abonnez-vous à une marque', navigateTo: 'brand_of_day', hint: 'subscribe' },
];

// Glass shatter particle system
function GlassShatterParticles({ onComplete }) {
    // Generate random glass shard particles
    const shards = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 300,
        y: (Math.random() - 0.5) * 200,
        rotate: Math.random() * 360,
        scale: 0.3 + Math.random() * 0.7,
        delay: Math.random() * 0.15,
    }));

    return (
        <m.div
            className="absolute inset-0 z-20 pointer-events-none overflow-visible"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            onAnimationComplete={onComplete}
        >
            {shards.map(shard => (
                <m.div
                    key={shard.id}
                    className="absolute"
                    style={{
                        left: '50%',
                        top: '50%',
                        width: 8 + Math.random() * 16,
                        height: 6 + Math.random() * 12,
                        background: `linear-gradient(${Math.random() * 180}deg, 
                            rgba(217, 119, 6, ${0.4 + Math.random() * 0.4}), 
                            rgba(255, 255, 255, ${0.2 + Math.random() * 0.3}))`,
                        clipPath: `polygon(${Math.random()*30}% 0%, 100% ${Math.random()*30}%, ${70+Math.random()*30}% 100%, 0% ${70+Math.random()*30}%)`,
                        boxShadow: '0 0 6px rgba(217, 119, 6, 0.5)',
                    }}
                    initial={{ x: 0, y: 0, rotate: 0, scale: 1, opacity: 1 }}
                    animate={{
                        x: shard.x,
                        y: shard.y,
                        rotate: shard.rotate,
                        scale: 0,
                        opacity: 0,
                    }}
                    transition={{
                        duration: 0.6 + Math.random() * 0.3,
                        delay: shard.delay,
                        ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                />
            ))}
            {/* Central flash */}
            <m.div
                className="absolute inset-0"
                style={{
                    background: 'radial-gradient(circle at center, rgba(217, 119, 6, 0.6) 0%, transparent 70%)',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 2] }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
            />
        </m.div>
    );
}

const TaskItem = ({ task, isCompleted, newlyCompleted, onClick }) => {
    const [showParticles, setShowParticles] = useState(false);

    useEffect(() => {
        if (newlyCompleted) {
            setShowParticles(true);
        }
    }, [newlyCompleted]);

    return (
        <m.button
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={!isCompleted ? onClick : undefined}
            className={`relative overflow-visible rounded-xl p-4 mb-3 flex items-center gap-3 border w-full text-left transition-colors ${
                isCompleted 
                ? 'bg-amber-500/10 border-amber-500/30 cursor-default' 
                : 'bg-white dark:bg-[#15171f] border-gray-200 dark:border-[#272a38] shadow-sm cursor-pointer hover:border-amber-500/40 active:scale-[0.98]'
            }`}
        >
            {/* Glass shatter particles */}
            <AnimatePresence>
                {showParticles && (
                    <GlassShatterParticles onComplete={() => setShowParticles(false)} />
                )}
            </AnimatePresence>

            {/* Overlay that shatters away */}
            <AnimatePresence>
                {newlyCompleted && (
                    <m.div
                        className="absolute inset-0 bg-white/20 backdrop-blur-lg z-10 rounded-xl"
                        initial={{ opacity: 1, scale: 1 }}
                        animate={{ 
                            opacity: [1, 0.8, 0],
                            scale: [1, 1.05, 1.1],
                        }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                    />
                )}
            </AnimatePresence>

            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors ${
                isCompleted 
                ? 'bg-amber-600 border-amber-600 text-white' 
                : 'border-gray-300 dark:border-gray-600 text-transparent'
            }`}>
                <Check className="w-4 h-4" />
            </div>
            
            <span className={`text-sm font-medium flex-1 ${isCompleted ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-200'}`}>
                {task.label}
            </span>

            {!isCompleted && (
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
            )}
        </m.button>
    );
};

export function OnboardingChecklist({ focusBrand }) {
    const navigate = useNavigate();
    const { completedTasks, isLoaded } = useOnboarding();
    const [hallOfFameProduct, setHallOfFameProduct] = useState(null);
    const [justCompleted, setJustCompleted] = useState([]);
    const [prevCompleted, setPrevCompleted] = useState([]);

    const allCompleted = ONBOARDING_TASKS.every(t => completedTasks.includes(t.id));

    useEffect(() => {
        if (!isLoaded) return;
        // Detect newly completed tasks to trigger animation
        const newCompletions = completedTasks.filter(t => !prevCompleted.includes(t));
        if (newCompletions.length > 0) {
            setJustCompleted(newCompletions);
            setTimeout(() => setJustCompleted([]), 2000); // Reset after animation
        }
        setPrevCompleted(completedTasks);
    }, [completedTasks, isLoaded]);

    useEffect(() => {
        if (allCompleted) {
            apiClient.entities.Product.hallOfFame().then(setHallOfFameProduct).catch(console.error);
        }
    }, [allCompleted]);

    const handleTaskClick = (task) => {
        if (task.navigateTo === 'brand_of_day') {
            // Navigate to the brand of the day page
            if (focusBrand?.slug) {
                navigate(`/brand/${focusBrand.slug}`, { 
                    state: { highlight: task.hint || null } 
                });
            } else {
                navigate('/search');
            }
        } else {
            navigate(task.navigateTo, { 
                state: { highlight: task.hint || null }
            });
        }
    };

    if (!isLoaded) return null;

    if (allCompleted && hallOfFameProduct) {
        return (
            <m.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-stone-900 border border-amber-500/30 rounded-2xl p-6 mb-8 relative overflow-hidden"
            >
                <div className="flex items-center gap-3 mb-4">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                    <h2 className="text-xl font-bold text-white">Quête Terminée !</h2>
                </div>
                <p className="text-gray-300 mb-6 text-sm">
                    Félicitations ! Vous avez débloqué la Pépite d'Or de la communauté : le produit le plus liké de la plateforme.
                </p>
                <div 
                    className="flex items-center gap-4 bg-stone-800 rounded-xl p-4 cursor-pointer hover:bg-stone-700/80 transition-colors border border-stone-700"
                    onClick={() => navigate(`/product?id=${hallOfFameProduct.id}`, { state: { from: 'home' } })}
                >
                    <img 
                        src={hallOfFameProduct.images?.[0] || ''} 
                        alt={hallOfFameProduct.name} 
                        className="w-20 h-24 object-cover rounded-lg"
                    />
                    <div>
                        <h3 className="font-semibold text-white">{hallOfFameProduct.name}</h3>
                        <p className="text-sm text-gray-400">{hallOfFameProduct.brand}</p>
                        <p className="text-amber-400 font-medium mt-1">{hallOfFameProduct.price}€</p>
                    </div>
                </div>
            </m.div>
        );
    }

    if (allCompleted) return null; // Loading Hall of fame

    return (
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Votre quête initiatique</h2>
            </div>
            <div className="space-y-1">
                {ONBOARDING_TASKS.map(task => (
                    <TaskItem 
                        key={task.id} 
                        task={task} 
                        isCompleted={completedTasks.includes(task.id)} 
                        newlyCompleted={justCompleted.includes(task.id)}
                        onClick={() => handleTaskClick(task)}
                    />
                ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{completedTasks.length} / {ONBOARDING_TASKS.length} accomplies</span>
                <div className="w-1/2 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <m.div 
                        className="h-full bg-amber-600"
                        initial={{ width: 0 }}
                        animate={{ width: `${(completedTasks.length / ONBOARDING_TASKS.length) * 100}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
